import { useEffect, useState } from "react";
import { Handshake, Plus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";

export default function BrandManager() {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [logoUrl, setLogoUrl] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/brands");
            setBrands(data.items || []);
        } catch (e) {
            toast.error("Failed to load brands");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const add = async () => {
        if (!name.trim()) return;
        try {
            await http.post("/brands", {
                name: name.trim(),
                category: category.trim() || null,
                logo_url: logoUrl.trim() || null,
                enabled: true,
                display_order: brands.length,
            });
            setName(""); setCategory(""); setLogoUrl("");
            await load();
            toast.success("Brand added");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this brand?")) return;
        try {
            await http.delete(`/brands/${id}`);
            setBrands((prev) => prev.filter((b) => b.id !== id));
            toast.success("Removed");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="card-surface p-0 overflow-hidden" data-testid="brand-manager">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <Handshake className="w-4 h-4 text-emerald-300" />
                    <h3 className="text-white font-semibold">Approved brand partners</h3>
                    <span className="chip ml-2">{brands.length}</span>
                </div>
                <button onClick={load} className="btn-ghost !py-1.5 !min-h-0 text-xs" data-testid="brand-mgr-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>
            <div className="p-5">
                <p className="text-xs text-slate-400 mb-3">
                    Only add brands you're authorized to represent and display. Public site shows these in the "National brands I partner with" strip.
                </p>
                <div className="grid gap-2 sm:grid-cols-4">
                    <input className="input-dark text-sm" placeholder="Brand name" value={name} onChange={(e) => setName(e.target.value)} data-testid="brand-name-input" />
                    <input className="input-dark text-sm" placeholder="Category (optional)" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="brand-category-input" />
                    <input className="input-dark text-sm sm:col-span-1" placeholder="Logo URL (optional)" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} data-testid="brand-logo-input" />
                    <button onClick={add} className="btn-primary !py-2 !min-h-0 text-sm" data-testid="brand-add-btn">
                        <Plus className="w-4 h-4" /> Add
                    </button>
                </div>

                {brands.length > 0 && (
                    <ul className="mt-4 divide-y divide-white/5 border border-white/5 rounded-xl overflow-hidden">
                        {brands.map((b) => (
                            <li key={b.id} className="flex items-center gap-3 px-4 py-3" data-testid={`brand-row-${b.id}`}>
                                {b.logo_url ? (
                                    <img src={b.logo_url} alt={b.name} className="h-6 object-contain" />
                                ) : (
                                    <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 inline-flex items-center justify-center text-emerald-300 text-xs font-black">
                                        {b.name.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-white truncate">{b.name}</div>
                                    {b.category && <div className="text-xs text-slate-400 truncate">{b.category}</div>}
                                </div>
                                <button onClick={() => remove(b.id)} className="text-slate-400 hover:text-red-400 text-xs inline-flex items-center gap-1" data-testid={`brand-del-${b.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" /> Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
